import { BottomNavigation, BottomNavigationAction } from '@mui/material';
import { Link } from 'react-router-dom';
import type { NavSection, NavSectionId } from '../navSections';
import { MORE_SHEET_ID, TAB_BAR_HEIGHT_PX } from '../chrome';
import { CloseIcon, MenuIcon, TAB_ICONS } from './icons';

interface BottomNavProps {
    sections: NavSection[];
    activeId: NavSectionId | null;
    moreOpen: boolean;
    /** Toggles the More sheet: More while it is shut, Close while it is open. */
    onMore: () => void;
}

// The tab bar as drawn in the mockups: 12 px labels (the selected one medium
// weight), icon and label 3 px apart, every tab laid out the same way.
// MUI's defaults make the selected label 14 px and its tab 6 px taller at the
// top, and a <button> (More) does not inherit the line height and letter
// spacing a link does: the four icons used to sit at three different heights.
const TAB_BAR_SX = {
    height: TAB_BAR_HEIGHT_PX,
    bgcolor: 'transparent',
    '& .MuiBottomNavigationAction-root': {
        minWidth: 0,
        maxWidth: 'none',
        padding: 0,
        gap: '3px',
        lineHeight: 'normal',
        letterSpacing: 'normal',
        '&.Mui-selected': { paddingTop: 0 },
    },
    '& .MuiBottomNavigationAction-label': {
        fontSize: 12,
        lineHeight: 'normal',
        letterSpacing: 'normal',
        '&.Mui-selected': { fontSize: 12, fontWeight: 500 },
    },
} as const;

// The phone tab bar: one tab per section the user may open, then More (Close
// while the More sheet is open).
export function BottomNav({
    sections,
    activeId,
    moreOpen,
    onMore,
}: BottomNavProps) {
    const value = moreOpen ? 'more' : activeId;
    const MoreIcon = moreOpen ? CloseIcon : MenuIcon;
    return (
        <BottomNavigation
            component="nav"
            aria-label="Main"
            showLabels
            value={value}
            sx={TAB_BAR_SX}
        >
            {sections.map(section => {
                const Icon = TAB_ICONS[section.id];
                return (
                    <BottomNavigationAction
                        key={section.id}
                        value={section.id}
                        label={section.label}
                        icon={<Icon />}
                        component={Link}
                        to={section.to}
                        aria-current={
                            section.id === activeId ? 'page' : undefined
                        }
                    />
                );
            })}
            <BottomNavigationAction
                value="more"
                label={moreOpen ? 'Close' : 'More'}
                icon={<MoreIcon />}
                onClick={onMore}
                aria-haspopup="dialog"
                aria-expanded={moreOpen}
                aria-controls={MORE_SHEET_ID}
            />
        </BottomNavigation>
    );
}
